import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useModal } from "hooks/useModal";

import { ModalRenderType } from "components/Modal/Modal.model";

import { TeacherModals } from "constants/modals-constants";
import { MISC_PAGE_ROUTES } from "constants/routes";

import { Divider } from "components/Divider/Divider";
import { FeedbackMessage } from "components/FeedbackMessage";
import { LoadingSpinner } from "components/LoadingSpinner";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphMedium } from "styles/elements/Texts";
import { CssFlex, CssIcon } from "styles/helpers/layout";

import type { IndividualInvite } from "../IndividualInviteList/IndividualInviteList";
import { IndividualInviteList } from "../IndividualInviteList/IndividualInviteList";
import { InviteLinkDetails } from "../InviteLinkDetails/InviteLinkDetails";

import { StyledModal, StyledSupportButton } from "./LinkInviteDetailsModal.styles";

interface LinkInviteDetailsModalParams {
  linkInviteId: string;
}

export const LinkInviteDetailsModal: React.FC = observer(() => {
  const { t } = useTranslation("teacher-dashboard");
  const { isModalOpen, closeModal, modalParams } = useModal();
  const { getCustomSupportEmailByDomain } = useDomainHandler();
  const { linkInviteDetails, fetchLinkInviteDetails, setLinkInviteDetails } = useInviteManagement();

  const isCurrentlyOpen = isModalOpen(TeacherModals.ViewLinkInviteDetails);
  const { linkInviteId } = modalParams as unknown as LinkInviteDetailsModalParams;

  useEffect(() => {
    if (isCurrentlyOpen && linkInviteId) {
      fetchLinkInviteDetails(linkInviteId);
    }

    if (!isCurrentlyOpen) {
      setLinkInviteDetails(null);
    }
  }, [isCurrentlyOpen, linkInviteId, fetchLinkInviteDetails, setLinkInviteDetails]);

  if (!linkInviteId) {
    return null;
  }

  const buildSupportEmailHref = (inviteLinkId: string): string => {
    const inviteLinkUrl = `${window.location.origin}${buildUrlWithPathParams(
      MISC_PAGE_ROUTES.CLAIM_LINK_INVITE_LICENSE,
      {
        inviteLinkId,
      },
    )}`;

    const subject = encodeURIComponent(
      t(
        "teacher-dashboard:invitationManagement.linkInviteDetailsModal.contactSupport.emailSubject",
        { defaultValue: "Request for License Cancellation" },
      ),
    );
    const body = encodeURIComponent(
      `${t(
        "teacher-dashboard:invitationManagement.linkInviteDetailsModal.contactSupport.emailBody",
        {
          defaultValue:
            "Hello,\n\nI would like to request a license cancellation for the following invite link:\n",
        },
      )}\n${inviteLinkUrl}`,
    );

    return `mailto:${getCustomSupportEmailByDomain()}?subject=${subject}&body=${body}`;
  };

  const handleContactSupportClick = (inviteLinkId: string): void => {
    window.open(buildSupportEmailHref(inviteLinkId), "_self");
  };

  const hasClaimingUsers = (linkInviteDetails?.claimingUserList?.length ?? 0) > 0;

  return (
    <StyledModal
      closeOnClickOutside
      dataCy={TeacherModals.ViewLinkInviteDetails}
      isOpen={isCurrentlyOpen}
      renderToElementType={ModalRenderType.REACT_PORTAL}
      onClose={closeModal}
    >
      <CssFlex flexDirection="column" gap={1.5} padding="2rem">
        <Heading2>
          {t("teacher-dashboard:invitationManagement.linkInviteDetailsModal.title")}
        </Heading2>
        <Divider />
        {linkInviteDetails?.id === linkInviteId ? (
          <CssFlex flexDirection="column" gap={1.5}>
            <InviteLinkDetails
              invitationId={linkInviteDetails.id}
              usage={{
                current: linkInviteDetails.currentUsage,
                max: linkInviteDetails.maxUsageLimit,
              }}
              products={linkInviteDetails.productNames}
              educationName={linkInviteDetails.educationName}
              schoolName={linkInviteDetails.schoolName}
              groupName={linkInviteDetails.groupName ?? null}
              licenseExpirationDate={new Date(linkInviteDetails.licenseExpirationDate)}
            />

            {/* -- Contact Support Section -- */}
            {hasClaimingUsers && (
              <FeedbackMessage
                variant="warning"
                dataCy="contact-support-warning-action"
                title={t(
                  "teacher-dashboard:invitationManagement.linkInviteDetailsModal.contactSupport.title",
                )}
                description={
                  <>
                    <ParagraphMedium>
                      {t(
                        "teacher-dashboard:invitationManagement.linkInviteDetailsModal.contactSupport.description",
                      )}
                    </ParagraphMedium>
                    <StyledSupportButton
                      variant="default"
                      dataCy="contact-support-button"
                      onClick={() => handleContactSupportClick(linkInviteDetails.id)}
                    >
                      <CssFlex alignItems="center" gap={0.5}>
                        <CssIcon iconName="fas fa-headset" />
                        {t(
                          "teacher-dashboard:invitationManagement.linkInviteDetailsModal.contactSupport.button",
                        )}
                      </CssFlex>
                    </StyledSupportButton>
                  </>
                }
              />
            )}

            {/* -- Claiming user list -- */}
            <IndividualInviteList
              inviteList={linkInviteDetails.claimingUserList.map(
                (user): IndividualInvite => ({
                  id: user.id,
                  email: user.email,
                  firstName: user.firstName,
                  middleName: user.middleName,
                  lastName: user.lastName,
                  dateField: user.claimedOn,
                  inTargetGroup: user.inTargetGroup,
                }),
              )}
              dateColumnTitle={t(
                "teacher-dashboard:invitationManagement.linkInviteDetailsModal.claimingUserList.columns.date",
              )}
              noResultsMessage={t(
                "teacher-dashboard:invitationManagement.linkInviteDetailsModal.claimingUserList.noResultsMessage",
              )}
              showGroupColumn={!!linkInviteDetails.groupName}
              readOnly
            />
          </CssFlex>
        ) : (
          <CssFlex alignItems="center" justifyContent="center" gap={0.5}>
            <LoadingSpinner />
          </CssFlex>
        )}
      </CssFlex>
    </StyledModal>
  );
});
